import { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { getConsent, setConsent, updateGaConsent, trackPageview } from '../lib/analytics';

/**
 * GaPageTracker — mały komponent śledzący zmiany trasy i wysyłający
 * page_view do GA (no-op gdy GA nie jest załadowane / user nie wyraził zgody).
 * Musi być wewnątrz <BrowserRouter> — korzysta z useLocation.
 */
export function GaPageTracker() {
  const { pathname, search } = useLocation();

  useEffect(() => {
    trackPageview(pathname + search);
  }, [pathname, search]);

  return null;
}

/**
 * CookieConsent — banner RODO (Consent Mode v2).
 * Tag gtag.js jest zawsze w index.html z domyślną zgodą `denied`; tutaj
 * aktualizujemy zgodę: „Akceptuję" → analytics granted, „Odrzucam" → denied.
 * Musi być wewnątrz <BrowserRouter>.
 */
export function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const consent = getConsent();
    if (consent === 'granted') {
      updateGaConsent(true);
    } else if (consent === null) {
      setVisible(true);
    }
    // consent === 'denied' → domyślny denied z index.html, nic nie robimy
  }, []); // tylko na mount

  function handleAccept() {
    setConsent('granted');
    updateGaConsent(true);
    setVisible(false);
  }

  function handleReject() {
    setConsent('denied');
    updateGaConsent(false);
    setVisible(false);
  }

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Zgoda na pliki cookie"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      className="fixed bottom-0 left-0 right-0 z-[9999] border-t-[1.5px] border-line bg-card shadow-lg sm:bottom-4 sm:left-auto sm:right-4 sm:w-[23rem] sm:border-[1.5px]"
    >
      {/* Karta w rogu (sm+) / niski pasek (telefon) — pełnoszeroki baner
          zasłaniał dół hero razem z przyciskami CTA. */}
      <div className="flex flex-col gap-3 px-4 py-3 sm:p-4">
        <p className="text-[13px] leading-5 text-ink-soft">
          Używamy plików cookie. Niezbędne są zawsze aktywne; analityczne
          (Google Analytics) włączymy tylko za Twoją zgodą, aby ulepszać
          Pinveo.{' '}
          <Link
            to="/privacy"
            className="font-semibold text-ink underline underline-offset-2 hover:opacity-80"
          >
            Polityka prywatności
          </Link>
        </p>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={handleReject}
            className="btn-secondary flex-1"
          >
            Odrzucam
          </button>
          <button
            type="button"
            onClick={handleAccept}
            className="btn-primary flex-1"
          >
            Akceptuję
          </button>
        </div>
      </div>
    </div>
  );
}
